import React, { useState } from "react";
import { MessageCircle } from "lucide-react";
import { MEDIA, WHATSAPP_URL } from "../../config/site";
import { Reveal, useEnquiry } from "../../lib/site-ui";

export const Founder = () => {
  const { openEnquiry } = useEnquiry();
  const [revealed, setRevealed] = useState(false);
  return (
    <section id="founder" className="py-24 sm:py-32 lg:py-40 border-t border-white/10" data-testid="founder-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <Reveal as="p" className="label-mono text-slate-500 mb-4">05 — The Instructor</Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center mt-8">
          <Reveal className="lg:col-span-5">
            <div
              className="relative group cursor-pointer select-none"
              onClick={() => setRevealed((v) => !v)}
              data-testid="founder-image-wrap"
            >
              <div className="absolute -inset-3 border border-white/10 -z-10" />
              <div className="relative overflow-hidden">
                <img
                  src={MEDIA.founder}
                  alt="Sheeroy Vaid — Founder of AeroVaid Aviation Academy"
                  className="w-full aspect-[3/4] object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                  data-testid="founder-image"
                />
                {/* Cinematic tint + vignette — fades away on hover (desktop) / tap (mobile) */}
                <div
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-0 transition-opacity duration-[650ms] ease-out group-hover:opacity-0 ${revealed ? "opacity-0" : "opacity-100"}`}
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 40%, rgba(2,6,23,0.22) 0%, rgba(2,6,23,0.40) 52%, rgba(2,6,23,0.74) 100%)",
                  }}
                />
                {/* Soft edge vignette that stays for a premium framed feel */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{ boxShadow: "inset 0 0 70px 10px rgba(2,6,23,0.45)" }}
                />
              </div>
              <span className="absolute bottom-0 left-0 bg-slate-950 border border-white/10 px-4 py-3 z-10">
                <span className="label-mono text-slate-500 block">Founder</span>
                <span className="font-display text-white text-lg leading-none">Sheeroy Vaid</span>
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">Meet Your Instructor</h2>
            <p className="mt-3 label-mono text-slate-400">Sheeroy Vaid · Founder, AeroVaid Aviation Academy · Student Pilot</p>
            <div className="mt-8 space-y-5 text-slate-300 leading-relaxed text-lg max-w-2xl">
              <p>
                AeroVaid was created to make DGCA preparation more accessible, structured and genuinely understandable
                for aspiring pilots. As a student pilot himself, Sheeroy knows how overwhelming ground school can feel
                when concepts are rushed or taught only for rote memorisation.
              </p>
              <p>
                The idea is simple: teach every subject clearly, give each student real attention, and keep the learning
                focused on the way DGCA examinations actually test you — all at a price that keeps quality coaching within
                reach for serious aspiring pilots.
              </p>
            </div>
            <button
              data-testid="talk-instructor-btn"
              onClick={() => openEnquiry({ message: "I'd like to talk to the instructor." })}
              className="mt-9 inline-flex items-center gap-2 border border-white/25 text-white px-8 py-4 rounded-sm hover:bg-white/5 transition-colors text-sm tracking-wide"
            >
              <MessageCircle className="w-4 h-4" /> TALK TO THE INSTRUCTOR
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const trustPoints = [
  "Direct instructor interaction",
  "Personalised attention",
  "Transparent pricing",
  "Flexible online and offline options",
  "Free demo before enrolment",
];

export const Trust = () => (
  <section className="py-24 sm:py-32 lg:py-40 border-t border-white/10 bg-slate-900/30" data-testid="trust-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal>
          <p className="label-mono text-slate-500 mb-4">06 — For Students & Parents</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
            A Better Way to Prepare for Your DGCA Journey
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed text-lg max-w-xl">
            Choosing a ground-school provider is about understanding the teaching approach, receiving proper support and
            having a clear preparation plan — not just picking the biggest name.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="flex flex-col justify-center">
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {trustPoints.map((p, i) => (
              <li key={p} className="flex items-center gap-5 py-5">
                <span className="label-mono text-slate-600">0{i + 1}</span>
                <span className="text-white text-lg">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="mt-16 border border-dashed border-white/15 p-10 lg:p-14 text-center" data-testid="student-experiences">
          <p className="label-mono text-slate-500 mb-4">Student Experiences</p>
          <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Genuine student testimonials will be added here as AeroVaid's first batches progress. We believe real
            experiences matter more than manufactured reviews.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
